/**
 * ASSET-4 — reading a serial off a label with the phone's camera.
 *
 * ASSET-3 shipped the scan that always works: a hand-held scanner is a
 * keyboard, it types the label and presses Enter, and nothing about that needs
 * a permission prompt. This is the other half, for the tech who does not have
 * one in the van — point the phone at the box instead of typing twenty
 * characters one-handed on a ladder.
 *
 * Three things make it more than a wrapper around a library.
 *
 * **The decoder is only downloaded where it is needed.** Chrome on Android has
 * `BarcodeDetector` built in, so on most phones this costs nothing at all. iOS
 * Safari has never shipped it, so there a WebAssembly decoder stands in — and
 * it is close to a megabyte, which is not something to pull down silently over
 * a tech's mobile data. `readiness()` says which case a device is in and the
 * screen asks before fetching.
 *
 * **It is served from here, never from a CDN.** The library's default is to
 * fetch its `.wasm` from jsdelivr, which would make camera scanning depend on
 * the public internet — on a platform whose entire premise is that a comms
 * room with no signal is the normal case, and which is often deployed on a box
 * with no route out at all. The override below points it at the copy Vite
 * emits beside the app, so the service worker caches it like any other asset
 * and a second scan works underground.
 *
 * **A decoded barcode is not a serial.** Code 128 and GS1 labels carry symbology
 * identifiers and field separators that the keyboard path never sees, because a
 * hand-held scanner strips them in firmware. `serialFrom` does the same job
 * here, so both paths hand `commission` the same string.
 */

/** What the camera found, or why there is nothing to find it with. */
export type Readiness =
  | {
      ok: true;
      /** True when the decoder has to be fetched before the first scan. */
      needsDecoder: boolean;
    }
  | { ok: false; reason: string };

/** The subset of `BarcodeDetector` this app uses. */
export type Detector = {
  detect(source: CanvasImageSource): Promise<{ rawValue: string }[]>;
};

/**
 * The symbologies worth looking for on security hardware.
 *
 * Narrowed on purpose. Every extra format is another decode attempted on every
 * frame, and a scanner that is slow to lock on is one a tech gives up on and
 * types instead. Code 128 is what almost every manufacturer prints; Code 39 is
 * what the older ones do; QR and Data Matrix turn up on panels and on the small
 * labels that have no room for bars.
 */
export const FORMATS = ["code_128", "code_39", "qr_code", "data_matrix", "ean_13", "itf"] as const;

/**
 * Strip what the symbology adds, leaving the serial as it is printed.
 *
 * A hand-held scanner does this in firmware before it types anything, which is
 * why the keyboard path never had to. A camera decode hands over the raw
 * symbol, which can carry:
 *
 * * an AIM symbology identifier — `]C1`, `]d2`, `]Q0` — prepended by the
 *   decoder to say what it read;
 * * GS1 group separators (ASCII 29), which print as nothing and paste as
 *   nothing, so a serial carrying one looks identical to one that does not and
 *   will never match;
 * * a label's own prefix: `S/N:`, `SN:`, `SERIAL:`;
 * * the trailing carriage return a scanner would have turned into Enter.
 *
 * What it deliberately does *not* do is fold case. Some manufacturers use
 * mixed-case serials, and printing back something the customer cannot find on
 * the label helps nobody — the same rule the server's `normalise` follows, and
 * comparison folds case at both ends anyway.
 */
export function serialFrom(raw: string): string {
  let text = raw.replace(/^\][A-Za-z][0-9]/, "");
  // Group and record separators, and the other invisibles a label can carry.
  text = text.replace(/[\u001c-\u001f]/g, " ");
  // Separated by punctuation or just a space — labels print both `S/N: X`
  // and `S/N X`. Requiring one or the other is what stops a serial that
  // merely starts with those letters, like `SNX-4410`, losing its first two.
  text = text.replace(/^\s*(?:s\s*\/\s*n|sn|serial(?:\s*(?:no|number))?)(?:\s*[:.#-]\s*|\s+)/i, "");
  return text.split(/\s+/).filter(Boolean).join(" ");
}

/**
 * Whether this device can scan, and what it would take.
 *
 * Pure and synchronous: it asks the browser what it has, not for permission.
 * The reasons are written to be read by a tech on a ladder, so each one says
 * what to do instead rather than naming an API.
 */
export function readiness(): Readiness {
  const nav = globalThis.navigator as Navigator | undefined;
  if (nav === undefined) return { ok: false, reason: "This device has no camera." };

  // The same cause as D54's sign-in failure: over plain http the browser hides
  // the camera, the microphone and the clipboard, and `mediaDevices` is simply
  // not there. Worth naming, because the fix is to open the https address and
  // nothing about the phone is wrong.
  if (!nav.mediaDevices?.getUserMedia) {
    const insecure = typeof isSecureContext === "boolean" && !isSecureContext;
    return {
      ok: false,
      reason: insecure
        ? "The camera needs the https address — open Keystone on it and scanning will work."
        : "This browser won't give Keystone the camera.",
    };
  }

  return { ok: true, needsDecoder: typeof native() !== "function" };
}

type NativeDetector = new (options: { formats: readonly string[] }) => Detector;

/** Read by value rather than by key: a browser may declare it and leave it
 * undefined, and so does a test that stubs it away. */
function native(): NativeDetector | undefined {
  return (globalThis as { BarcodeDetector?: NativeDetector }).BarcodeDetector;
}

let fallback: Promise<Detector> | null = null;

/**
 * A decoder, fetching one first if this browser has none.
 *
 * The fetch is remembered rather than repeated: the module and its WebAssembly
 * are a megabyte, and a tech who scans twelve cameras onto a job should pay for
 * it once per install, not once per camera.
 */
export async function detector(): Promise<Detector> {
  const Built = native();
  if (Built) return new Built({ formats: FORMATS });

  fallback ??= load();
  return fallback;
}

/** Thrown when the decoder itself could not be fetched — which, on a phone
 * that has never scanned before, is what being underground looks like. */
export class DecoderUnavailable extends Error {}

async function load(): Promise<Detector> {
  // Imported lazily, and only on the phones that need it, so the download is
  // paid for by iOS rather than by everyone. Vite emits it as its own chunk.
  let parts: [typeof import("barcode-detector/ponyfill"), { default: string }];
  try {
    parts = await Promise.all([import("barcode-detector/ponyfill"), import("zxing-wasm/reader/zxing_reader.wasm?url")]);
  } catch (cause) {
    // The chunk is cached by the service worker once it has been fetched
    // successfully, so this is the first scan on this phone *and* there is no
    // signal. Said plainly, because "couldn't start the camera" would send a
    // tech looking at their camera.
    fallback = null; // so the next attempt, with signal, tries again
    throw new DecoderUnavailable(
      "Keystone's barcode reader isn't on this phone yet and there's no signal to fetch it. Type the serial, " +
        "and scan next time you're on wifi.",
      { cause },
    );
  }
  const [{ BarcodeDetector, setZXingModuleOverrides }, wasm] = parts;
  // Served from this origin. The library's default points at a CDN, which a
  // comms room and an isolated NUC both fail to reach.
  setZXingModuleOverrides({ locateFile: () => wasm.default });
  return new BarcodeDetector({ formats: [...FORMATS] }) as unknown as Detector;
}

/** For tests, and for a device that has been told to forget the download. */
export function forgetDecoder(): void {
  fallback = null;
}

/**
 * The camera a tech wants, which is the one on the back.
 *
 * `environment` is a preference rather than a constraint on purpose: asked for
 * exactly, a laptop with only a front camera refuses outright, and a tech
 * holding a tablet gets an error instead of a picture.
 */
export const CAMERA: MediaStreamConstraints = {
  video: { facingMode: { ideal: "environment" }, width: { ideal: 1280 }, height: { ideal: 720 } },
  audio: false,
};

/** Why `getUserMedia` said no, in words worth showing someone. */
export function refusal(error: unknown): string {
  if (error instanceof DecoderUnavailable) return error.message;
  const name = (error as { name?: string } | null)?.name ?? "";
  if (name === "NotAllowedError" || name === "SecurityError") {
    return "Camera access was turned down. You can allow it in the browser's site settings, or type the serial.";
  }
  if (name === "NotFoundError" || name === "OverconstrainedError") {
    return "No camera on this device.";
  }
  if (name === "NotReadableError") {
    return "Something else is using the camera. Close it and try again.";
  }
  return "Couldn't start the camera.";
}
